import $ from 'jquery'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import Breadcrumbs from 'scoreboard/components/breadcrumbs'
import MultiTreeSelector from 'scoreboard/components/multi-tree-selector'
import projects from 'scoreboard/projects/projects'

/**
 * Displays an org tree, and if multiple projects exist a project selector.
 * Once an org is selected, the Project Label will also be rendered in each selected row.
 *
 * Keeps all project concerns out of MultiTreeSelector
 */
const OrgMultiTreeSelector = MultiTreeSelector.extend({

	/**
	 * @returns {Project}
	 */
	getProject: function(projectId) {
		return projects.get(projectId)
	},

	/**
	 * @returns {Number}
	 */
	projectOrder: function(projectId) {
		return this.getProject(projectId).get('order')
	},

	shouldShowProjectLabel: function() {
		return projects.isVisible()
	},

	/**
	 * @param {Number|String} options.projectId
	 */
	initialize: function(options = {}) {
		this.projectId = _.toNumber(options.projectId) || projects.id
		MultiTreeSelector.prototype.initialize.apply(this, arguments)
		this.selectedItemCache = {} // id:data for selected nodes

		this.listenTo(this.gardener, 'projectId', projectId => {
			this.gardener.show({projectId})
			this.projectId = projectId
		})
	},

	setupStream: function(stream) {
		this.orgData = []
		//capture the org data as it's streamed
		stream.on('data', data => {
			this.orgData.push(data)
		})
		MultiTreeSelector.prototype.setupStream.apply(this, arguments)
	},

	postRenderGardener: function() {
		MultiTreeSelector.prototype.postRenderGardener.apply(this, arguments)
		this.gardener.on('projectId', () => this.gardener.$('.finder input, .expand-collapse').attr('disabled', false))
	},

	showGardenerErrorMessage: function(emptyMessage) {
		if (this.gardener.showProjectSelector) {
			this.gardener.$('.tree-container .tree').addClass('is-not-displayed')
			this.gardener.$('.tree-container').append(emptyMessage)
			this.gardener.$('.finder input, .expand-collapse').attr('disabled', true)
		} else {
			MultiTreeSelector.prototype.showGardenerErrorMessage.apply(this, arguments)
		}
	},

	/**
	 * Creates a new row
	 * @return {jQuery}
	 */
	rowTemplate: function(ctx) {
		const row = MultiTreeSelector.prototype.rowTemplate.apply(this, arguments)
		row.attr('data-project-order', this.projectOrder(ctx.projectId))
		return row
	},

	/**
	 * @param rows all existing rows
	 * @param treeItem the data object provided to the gardener
	 * @param newOrder the order attribute from the newly selected item (leftBound)
	 * @return the node that a new row should be inserted before
	 */
	getRowInsertionPoint: function(rows, treeItem, newOrder) {
		const newProjectOrder = this.projectOrder(treeItem.projectId)
		return _.find(rows, row => {
			const {order, projectOrder} = $(row).data()
			const movedBeyondProject = projectOrder > newProjectOrder
			const movedBeyondOrgNode = order > newOrder
			return  movedBeyondProject || (projectOrder == newProjectOrder && movedBeyondOrgNode)
		})
	},

	/**
	 * Input is the tree node
	 */
	_buildRow: function({id, label: orgLabel, projectId}) {
		// After the row is created, append the project related labels
		let row = MultiTreeSelector.prototype._buildRow.apply(this, arguments)
		const projectLabel = this.getProject(projectId).get('label')
		const showProjectLabel = this.shouldShowProjectLabel()
		let extraClass = showProjectLabel ? '' : 'single-label'
		let projectName = showProjectLabel ? `<span class="project-name">${_.escape(projectLabel)}</span>` : ''
		let orgId = this.options.showId ? `(${i18n.get('common.id')} ${id})` : ''
		let rowDetails = `<span class="org-name ${extraClass}">${_.escape(orgLabel)} ${orgId}</span>` + projectName
		row.find('.table-cell-text').empty().append(rowDetails)
		return row
	},

	/**
	 * Because the org selector has selections across projects and we only have
	 * an org tree for one project at a time, we have to override this function
	 * in order to inject the required attributes (cascade, descendants)
	 * from the group.orgPermissions instead of the tree nodes.
	 *
	 * @param {Array} selectionAccumulator
	 * @param {Object} data All group data provided for the org-permissions
	 * 						(cascade, projectId, descendants)
	 * @param orgId
	 */
	transformSelection: function(selectionAccumulator, data, orgId) {
		const id = _.toNumber(orgId)
		this.selectedItemCache[id] = data
		selectionAccumulator.push(data)
	},

	selectionsSortBy: function() {
		return [
			({projectId}) => this.projectOrder(this.projectId), // first, by project sort order
			'order' // second, by org order (leftBound)
		]
	},

	initItemData: function(treeItem) {
		const data = _.extend(
			{projectId: this.projectId}, // Things needed for org selections
			MultiTreeSelector.prototype.initItemData.apply(this, arguments)) // Defaults
		this.selectedItemCache[treeItem.id] = data
		return data
	},

	/**
	 * @override Use the current org tree if it contains the target node, otherwise
	 * we have to lazy load the ancestors from the server.
	 */
	showInfoTooltip: function(e) {
		_.invoke(this.infoTooltip, 'remove')
		const {currentTarget, target} = e
		var id = $(currentTarget).closest('tr').data('id')
		const treeNode = this.gardener.tree.nodes[id]
		if (treeNode) {
			this.infoTooltip = Breadcrumbs.tooltip({
				id,
				target: e.currentTarget,
				gardener: this.gardener
			})
		} else {
			$.get(`api/organizations/${id}/ancestors`, objects => {
				this.infoTooltip = Breadcrumbs.tooltip({id, target, objects})
			})
		}
	}

})

export default OrgMultiTreeSelector

OrgMultiTreeSelector.__filename__ = 'organizations/org-multi-tree-selector.js'
